var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// libs
import React, { useEffect, useMemo } from 'react';
import Tile from '@/widgets/Tile';
import { useNavigate } from 'react-router-dom';
//dls
import { Text } from '@swiggy-private/dls-web-core/dist/components/Text';
// constants
import { DlsColorTokens, DlsFontVariants } from '@/constants/DLSTokens';
import Routes from '@/constants/Routes';
import { NULL } from '@/constants/Misc';
//stores
import useReadCampaignStore from '@/stores/ReadCampaign';
import useCampaignDetailsStore from '@/stores/CampaignDetails';
import useAccountStore from '@/stores/account';
import useConfigStore from '@/stores/Configs';
//style
import style from './index.module.scss';
//widgets
import EditButton from '@/widgets/EditButton/EditButton';
import KeywordTable from '@/pages/instamart/campaign/details/widgets/KeywordTable';
import CampaignDetails from './widgets/CampaignDetails/CampaignDetails';
import PerformanceMetrics from './widgets/PerformanceMetrics';
import ProductTable from './widgets/ProductTable';
import StaleCandidateTable from './widgets/StaleCandidateTable';
// defs
import { ROLES } from '@local/typedefs/dist/common/userRoles';
import { AdUnitType, } from '@local/typedefs/dist/shaped/campaign/Campaign';
// hooks
import useNotification from '@/hooks/useNotification';
//helpers
import { format } from '@/helpers/utils';
import { checkEditableStatus } from '@/helpers/campaign';
import { checkCampaignBudgetAndWarn, checkCampaignEndDateAndWarn, checkCampaignStartDateAndNotify, checkKeywordBidAndWarn, } from './helpers';
//components
import Tooltip from '@/components/Tooltip/Tooltip';
const IndexPageContent = () => {
    const navigate = useNavigate();
    const { campaign } = useReadCampaignStore((state) => ({
        campaign: state.campaign,
    }));
    const { aggregateMetrics, campaignPerformance, setCampaignPerformanceData, setCampaignPerformanceSelectedRange, } = useCampaignDetailsStore((store) => ({
        aggregateMetrics: store.aggregateMetrics,
        campaignPerformance: store.campaignPerformance,
        setCampaignPerformanceData: store.setCampaignPerformanceData,
        setCampaignPerformanceSelectedRange: store.setCampaignPerformanceSelectedRange,
    }));
    const { permissions, getCurrentAccountId } = useAccountStore((store) => ({
        permissions: store.loggedInAccountDetails.permissions,
        getCurrentAccountId: store.getCurrentAccountId,
    }));
    const { campaignOpDisabled, campaignOpDisabledCallout } = useConfigStore((state) => ({
        campaignOpDisabled: state.campaignOpDisabled,
        campaignOpDisabledCallout: state.campaignOpDisabledCallout,
    }));
    const { notify } = useNotification();
    const haveCampaignEditPermission = useMemo(() => permissions.includes(ROLES.EDIT_CAMPAIGN), [permissions]);
    const canEditCampaign = haveCampaignEditPermission && checkEditableStatus(campaign === null || campaign === void 0 ? void 0 : campaign.status);
    useEffect(() => {
        (() => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            checkCampaignStartDateAndNotify({
                notify,
                campaignStatus: campaign === null || campaign === void 0 ? void 0 : campaign.status,
                startDate: campaign === null || campaign === void 0 ? void 0 : campaign.startDate,
            });
            checkCampaignBudgetAndWarn({
                notify,
                campaignStatus: campaign === null || campaign === void 0 ? void 0 : campaign.status,
                spent: (_a = aggregateMetrics === null || aggregateMetrics === void 0 ? void 0 : aggregateMetrics.budgetBurnt) !== null && _a !== void 0 ? _a : 0,
                budget: (campaign === null || campaign === void 0 ? void 0 : campaign.budget) || 0,
            });
            const accountId = yield getCurrentAccountId();
            checkKeywordBidAndWarn({
                notify,
                campaignStatus: campaign === null || campaign === void 0 ? void 0 : campaign.status,
                keywords: (_b = campaign === null || campaign === void 0 ? void 0 : campaign.adGroups[0].keywords) !== null && _b !== void 0 ? _b : [],
                placementIds: (_c = campaign === null || campaign === void 0 ? void 0 : campaign.placementIds) !== null && _c !== void 0 ? _c : [],
                candidateIds: (_d = campaign === null || campaign === void 0 ? void 0 : campaign.adGroups[0].candidates.map((c) => c.id)) !== null && _d !== void 0 ? _d : [],
                adUnitType: AdUnitType.PRODUCT,
                accountId,
            });
            checkCampaignEndDateAndWarn({
                notify,
                endDate: campaign === null || campaign === void 0 ? void 0 : campaign.endDate,
                campaignStatus: campaign === null || campaign === void 0 ? void 0 : campaign.status,
            });
        }))();
    }, [aggregateMetrics, campaign === null || campaign === void 0 ? void 0 : campaign.budget]);
    const redirectToKeywordEdit = () => {
        navigate(format(Routes.INSTAMART.CAMPAIGN_EDIT, (campaign === null || campaign === void 0 ? void 0 : campaign.id) || ''), {
            preventScrollReset: true,
            state: {
                activeStep: 3,
            },
        });
    };
    const redirectToProductEdit = () => {
        navigate(format(Routes.INSTAMART.CAMPAIGN_EDIT, (campaign === null || campaign === void 0 ? void 0 : campaign.id) || ''), {
            preventScrollReset: true,
            state: {
                activeStep: 2,
            },
        });
    };
    return (React.createElement("div", null,
        React.createElement(CampaignDetails, null),
        React.createElement(Tile, { className: style.tile },
            React.createElement("div", { className: style.tableTitleContainer },
                React.createElement("div", { className: style.tableHeader },
                    React.createElement(Text, { variant: DlsFontVariants.header_H4_Black, color: DlsColorTokens.text_High_Emphasis }, "Keyword Performance"),
                    canEditCampaign &&
                        (campaignOpDisabled ? (React.createElement(Tooltip, { content: campaignOpDisabledCallout, inline: false },
                            React.createElement(EditButton, { disabled: true, onClick: redirectToKeywordEdit, label: "Edit" }))) : (React.createElement(EditButton, { onClick: redirectToKeywordEdit, label: "Edit" })))),
                React.createElement(KeywordTable, { keywordData: campaign === null || campaign === void 0 ? void 0 : campaign.adGroups[0].keywords }))),
        React.createElement(Tile, { className: style.tile },
            React.createElement("div", { className: style.tableTitleContainer },
                React.createElement("div", { className: style.tableHeader },
                    React.createElement(Text, { variant: DlsFontVariants.header_H4_Black, color: DlsColorTokens.text_High_Emphasis }, "Products Performance"),
                    canEditCampaign &&
                        (campaignOpDisabled ? (React.createElement(Tooltip, { content: campaignOpDisabledCallout, inline: false },
                            React.createElement(EditButton, { disabled: true, onClick: redirectToProductEdit, label: "Edit" }))) : (React.createElement(EditButton, { onClick: redirectToProductEdit, label: "Edit" })))),
                React.createElement(ProductTable, { showHeader: true, productData: campaign === null || campaign === void 0 ? void 0 : campaign.adGroups[0].candidates }),
                campaign && (React.createElement(StaleCandidateTable, { staleCandidates: campaign.adGroups[0].staleCandidates })))),
        React.createElement(PerformanceMetrics, { performanceData: campaignPerformance, campaignId: (campaign === null || campaign === void 0 ? void 0 : campaign.id) || NULL, setPerformanceData: setCampaignPerformanceData, setSelectedRange: setCampaignPerformanceSelectedRange })));
};
export default IndexPageContent;
